'use client';

/**
 * FAQ Accordion Component
 *
 * Renders an interactive FAQ/Accordion section with collapsible items.
 */

import classNames from 'classnames';
import React, { useState } from 'react';

import type { FaqConfig } from '../types';

interface FaqAccordionProps {
  config: FaqConfig;
  className?: string;
}

export const FaqAccordion: React.FC<FaqAccordionProps> = ({ config, className }) => {
  // Track which items are open (by index)
  const [openItems, setOpenItems] = useState<Set<number>>(
    new Set(config.items.map((item, index) => (item.isDefaultOpen ? index : -1)).filter((i) => i >= 0)),
  );

  const toggleItem = (index: number) => {
    setOpenItems((prev) => {
      const newSet = new Set(prev);

      if (config.mode === 'accordion') {
        // Accordion mode: only one item open at a time
        if (newSet.has(index)) {
          newSet.delete(index);
        } else {
          newSet.clear();
          newSet.add(index);
        }
      } else {
        // Independent mode: toggle individual items
        if (newSet.has(index)) {
          newSet.delete(index);
        } else {
          newSet.add(index);
        }
      }

      return newSet;
    });
  };

  return (
    <div className={classNames('faq-accordion', className)}>
      {config.items.map((item, index) => {
        const isOpen = openItems.has(index);

        return (
          <div key={index} className="faq-item border-b border-[#F7931A]">
            <button
              onClick={() => toggleItem(index)}
              className="flex w-full cursor-pointer items-start justify-between py-5 text-left"
              aria-expanded={isOpen}
            >
              {/* Preserve all HTML tags and styles from WordPress */}
              <div className="flex-1" dangerouslySetInnerHTML={{ __html: item.trigger }} />

              <div className="ml-4 mt-1 flex-shrink-0">
                <FaqIcon icon={config.icon} isOpen={isOpen} />
              </div>
            </button>

            {isOpen && (
              // No extra classes - preserve WordPress styling
              <div className="pb-5" dangerouslySetInnerHTML={{ __html: item.content }} />
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * FAQ Icon Component
 */
interface FaqIconProps {
  icon: string;
  isOpen: boolean;
}

const FaqIcon: React.FC<FaqIconProps> = ({ icon, isOpen }) => {
  if (icon === 'plus') {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        className="transition-transform duration-200"
        style={{ transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)' }}
      >
        <path d="M8 4v8M4 8h8" stroke="#003049" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  // Default: chevron - points left when closed, top when open
  return (
    <svg
      width="16"
      height="16"
      fill="none"
      viewBox="0 0 16 16"
      className="transition-transform duration-200"
      style={{ transform: isOpen ? 'rotate(-90deg)' : 'rotate(-180deg)' }}
    >
      <path d="M6 4l4 4-4 4" stroke="#003049" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};
